import React from 'react';
import { Divider, Flex, TextV2, Box } from '@withjoy/joykit';
import { LocationDatesOccupants } from '@shared/components/LocationDatesOccupantsSearchBar/LocationDatesOccupantsSearchBar';
import { StaysSearchTerms } from '@apps/stays/types';
import { AccommodationsConsoleState, SortOption } from '@apps/accommodations/AccommodationsConsole.types';
import { ArrowLeft, Filter } from '@withjoy/joykit/icons';
import { format, parseISO } from 'date-fns';
import { useTranslation } from '@shared/core';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { getSearchFormInitialValue } from '@apps/accommodations/AccommodationsConsole.utils';
import { pxToRem } from '@withjoy/joykit/theme';
import { SortComponent } from '../ListingsPanel/components/SortComponent';

const Container = ({ children }: { children?: React.ReactNode }) => (
  <Flex alignItems="center" gap={4} padding={4} borderBottom="1px solid" background="white" position="sticky" top={0} zIndex={1000} borderColor="mono3">
    {children}
  </Flex>
);

const AccommodationsConsoleHeader = ({
  handleFormSubmit,
  state,
  onFilterPanelOpen,
  onSearchOpen,
  onClose,
  sortOption,
  onSortChange
}: {
  handleFormSubmit: (value: StaysSearchTerms) => void;
  state: AccommodationsConsoleState;
  onFilterPanelOpen: () => void;
  onSearchOpen: () => void;
  onClose?: () => void;
  sortOption: SortOption;
  onSortChange: (sort: SortOption) => void;
}) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  const searchFormInitialValue = getSearchFormInitialValue(state);

  return (
    <>
      {/* Header Section */}
      <Flex alignItems="center" gap={4} padding={4} borderBottom="1px solid" background="white" position="sticky" top={0} zIndex={1000} borderColor="mono3">
        {isMobileOrTablet ? (
          <MobileHeader state={state} onFilterPanelOpen={onFilterPanelOpen} onSearchOpen={onSearchOpen} onClose={onClose} />
        ) : (
          <DeskTopHeader handleFormSubmit={handleFormSubmit} searchFormInitialValue={searchFormInitialValue} />
        )}
      </Flex>
      {isMobileOrTablet && (
        <Flex paddingX={5} paddingY={3} zIndex={1000} justifyContent={'center'} backgroundColor={'white'} borderBottom="1px solid" borderColor="mono3">
          <SortComponent selectedValue={sortOption} onChange={onSortChange} />
        </Flex>
      )}
    </>
  );
};

const MobileHeader = ({
  state,
  onFilterPanelOpen,
  onSearchOpen,
  onClose
}: {
  state: AccommodationsConsoleState;
  onFilterPanelOpen: () => void;
  onSearchOpen: () => void;
  onClose?: () => void;
}) => {
  /**
   * {
   *   "problem": "When creating Date objects from date strings like '2024-01-15', JavaScript's Date constructor interprets them in the user's local timezone. This can cause dates to shift by one day in certain timezones, especially around midnight UTC transitions.",
   *   "solution": "Using parseISO from date-fns ensures proper ISO string parsing and handles timezone information correctly, preventing date shifting issues.",
   * }
   */
  const checkinDate = format(parseISO(state.searchDates.start), 'MMM dd');
  const checkoutDate = format(parseISO(state.searchDates.end), 'dd');
  const adults = state.occupancies.occupancies?.reduce((acc, occ) => acc + occ.adults, 0) || 0;
  const children = state.occupancies.occupancies?.reduce((acc, occ) => acc + occ.childrenAges.length, 0) || 0;
  const { t } = useTranslation('accommodationsConsole');
  const headerTrans = t('header');

  return (
    <Flex width={'100%'} justifyContent={'space-between'} alignItems={'center'} paddingX={3}>
      <ArrowLeft size={22} cursor={'pointer'} onClick={onClose} />
      <Flex
        onClick={onSearchOpen}
        cursor={'pointer'}
        alignItems={'center'}
        paddingX={7}
        paddingY={3}
        flexDirection={'column'}
        border={'1px solid'}
        borderColor={'mono3'}
        borderRadius="full"
      >
        <TextV2 typographyVariant={'label3'} fontWeight={600} maxWidth={pxToRem(150)} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
          {state.searchLocation}
        </TextV2>
        <Flex alignItems={'center'} marginTop={2} marginRight={4} gap={4}>
          <TextV2 typographyVariant={'label1'} color={'mono10'}>
            {checkinDate} — {checkoutDate}
          </TextV2>
          <Divider orientation="vertical" height={14} />
          <TextV2 typographyVariant={'label1'} color={'mono10'}>
            {headerTrans.rooms.rooms({ rooms: state.occupancies.unitCount, count: state.occupancies.unitCount })},{' '}
            {headerTrans.guests.guests({ guests: adults + children, count: adults + children })}
          </TextV2>
        </Flex>
      </Flex>
      <Filter size={22} cursor={'pointer'} onClick={onFilterPanelOpen} />
    </Flex>
  );
};

const DeskTopHeader = ({
  handleFormSubmit,
  searchFormInitialValue
}: {
  handleFormSubmit: (value: StaysSearchTerms) => void;
  searchFormInitialValue: Partial<StaysSearchTerms>;
}) => {
  return (
    <Box flex={1}>
      <LocationDatesOccupants onSubmit={handleFormSubmit} initialValue={searchFormInitialValue} />
    </Box>
  );
};

AccommodationsConsoleHeader.Skeleton = () => {
  return <Container />;
};

export { AccommodationsConsoleHeader };
